import axios from 'axios';
import { BaseURLVoyager, MockDocumentos } from '../../config';
import { getUser } from './Storage.Utils';

/**
 * Descarga de documentos (boletas de mantención y facturas).
 *
 * El front nunca conoce la URL del documento: le pide el archivo a nuestro
 * propio endpoint pasándole sólo identificadores, y el backend resuelve el path
 * contra `obtener_boletas_mnt` / `obtiene_factura`, baja el archivo y lo
 * devuelve como stream. Así el link "no queda visible al público" — no hay
 * href en el DOM ni path en las respuestas JSON — y de paso evitamos que el
 * navegador tenga que hablar con ventadigital.sendero.cl (que no envía CORS y
 * sirve las boletas por http).
 *
 * Se espera que el endpoint responda con el binario y, ojalá, con
 * `Content-Disposition: attachment; filename="..."`.
 *
 * Va por axios directo y no por `api/hookup` porque esa capa parsea JSON; acá
 * necesitamos el blob. Tampoco manda cabeceras de autenticación, igual que el
 * resto de la capa moderna: la identidad viaja en el `rut` de la sesión.
 */

const ENDPOINT = 'api/v2/documento';

export const BOLETA = 'boleta';
export const FACTURA = 'factura';

/**
 * Nombre de archivo a partir de la etiqueta visible ("Boleta Cuota N° 1"),
 * usado sólo si el backend no manda `Content-Disposition`.
 */
function nombreArchivo(nombre) {
	const base = String(nombre)
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/[^a-zA-Z0-9]+/g, '_')
		.replace(/^_+|_+$/g, '')
		.toLowerCase();
	return (base || 'documento') + '.pdf';
}

function nombreDesdeCabecera(response, porDefecto) {
	const cabecera =
		response.headers &&
		(response.headers['content-disposition'] ||
			response.headers['Content-Disposition']);
	if (!cabecera) {
		return porDefecto;
	}
	const match = cabecera.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
	return match ? decodeURIComponent(match[1]) : porDefecto;
}

function guardar(blob, nombre) {
	const objectUrl = window.URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = objectUrl;
	link.download = nombre;
	link.style.display = 'none';
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	// Revocar de inmediato cancela la descarga en algunos navegadores.
	setTimeout(() => window.URL.revokeObjectURL(objectUrl), 10000);
}

/**
 * @param {{tipo: string, contrato: string, movimiento?: string, cuota?: number}} params
 *        identificadores del documento; el path lo resuelve el backend.
 * @param {string} nombre etiqueta visible, ej. "Boleta Cuota N° 1"
 * @returns {Promise<{ok: boolean, mensaje?: string}>}
 */
export default async function descargarDocumento(params, nombre) {
	const archivo = nombreArchivo(nombre);
	const consulta = Object.assign({ rut: getUser().rut }, params);

	if (MockDocumentos) {
		return mockDescarga(consulta, nombre, archivo);
	}

	try {
		const response = await axios({
			method: 'get',
			baseURL: BaseURLVoyager,
			url: ENDPOINT,
			params: consulta,
			responseType: 'blob',
		});

		guardar(response.data, nombreDesdeCabecera(response, archivo));
		return { ok: true };
	} catch (error) {
		return { ok: false, mensaje: 'No se pudo descargar el documento' };
	}
}

/* ------------------------------------------------------------------------ *
 * Mock del endpoint de descarga, mientras no exista. Genera un PDF mínimo en
 * el navegador para que el flujo click -> archivo guardado se pueda probar
 * completo. Borrar junto con el flag REACT_APP_MOCK_DOCUMENTOS.
 * ------------------------------------------------------------------------ */

function mockDescarga(consulta, nombre, archivo) {
	const lineas = [
		'Documento simulado - el backend aun no esta disponible.',
		'',
		'Tipo: ' + consulta.tipo,
		'Contrato: ' + consulta.contrato,
		'RUT titular: ' + consulta.rut,
	];
	if (consulta.movimiento) {
		lineas.push('Movimiento de caja: ' + consulta.movimiento);
	}
	if (consulta.cuota) {
		lineas.push('Cuota: ' + consulta.cuota);
	}
	guardar(mockPdf(nombre, lineas), archivo);
	return Promise.resolve({ ok: true });
}

/** Sólo ASCII: el PDF se escribe byte a byte y los acentos romperían el largo. */
function mockAscii(texto) {
	return String(texto)
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/°/g, 'No ')
		.replace(/[\\()]/g, '')
		.replace(/[^\x20-\x7e]/g, '');
}

function mockRelleno(numero) {
	let texto = String(numero);
	while (texto.length < 10) {
		texto = '0' + texto;
	}
	return texto;
}

function mockPdf(titulo, lineas) {
	const flujo = ['BT /F1 18 Tf 60 760 Td (' + mockAscii(titulo) + ') Tj ET']
		.concat(
			lineas.map(
				(linea, i) =>
					'BT /F1 11 Tf 60 ' +
					(720 - i * 20) +
					' Td (' +
					mockAscii(linea) +
					') Tj ET'
			)
		)
		.join('\n');

	const objetos = [
		'<</Type/Catalog/Pages 2 0 R>>',
		'<</Type/Pages/Kids[3 0 R]/Count 1>>',
		'<</Type/Page/Parent 2 0 R/MediaBox[0 0 595 842]/Resources<</Font<</F1 5 0 R>>>>/Contents 4 0 R>>',
		'<</Length ' + flujo.length + '>>\nstream\n' + flujo + '\nendstream',
		'<</Type/Font/Subtype/Type1/BaseFont/Helvetica>>',
	];

	let pdf = '%PDF-1.4\n';
	const offsets = [];
	objetos.forEach((objeto, i) => {
		offsets.push(pdf.length);
		pdf += (i + 1) + ' 0 obj\n' + objeto + '\nendobj\n';
	});

	const inicioXref = pdf.length;
	pdf += 'xref\n0 ' + (objetos.length + 1) + '\n0000000000 65535 f \n';
	offsets.forEach((offset) => {
		pdf += mockRelleno(offset) + ' 00000 n \n';
	});
	pdf +=
		'trailer\n<</Size ' +
		(objetos.length + 1) +
		'/Root 1 0 R>>\nstartxref\n' +
		inicioXref +
		'\n%%EOF';

	return new Blob([pdf], { type: 'application/pdf' });
}
